/**
 * League fight reminders by Web Push (docs/game-design.md §7.4, docs/architecture.md §13): this
 * browser subscribes with the game server's key, and the server sends a reminder when a league
 * fight's window opens or an opponent is waiting. iPhone delivers pushes only to the game added
 * to the Home Screen.
 */
import { SERVER_HTTP } from '../net/config';
import { diag } from './diag';

export type PushAvailability = 'yes' | 'installFirst' | 'no';

/** Whether this browser can get reminders (iPhone: only from the Home Screen app). */
export function pushAvailability(): PushAvailability {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('Notification' in window)) {
    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
    return ios ? 'installFirst' : 'no';
  }
  return 'PushManager' in window ? 'yes' : 'no';
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

/** Whether reminders are on in this browser. */
export async function pushEnabled(): Promise<boolean> {
  if (pushAvailability() !== 'yes' || Notification.permission !== 'granted') return false;
  const reg = await registration();
  return !!(await reg?.pushManager.getSubscription());
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export type PushResult = 'on' | 'denied' | 'unavailable' | 'failed';

/** Turns reminders on: asks for permission, subscribes, tells the server. */
export async function enablePush(language: string): Promise<PushResult> {
  try {
    if (pushAvailability() !== 'yes') return 'unavailable';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';
    const reg = await registration();
    if (!reg) return 'unavailable';
    const { key } = (await (await fetch(SERVER_HTTP + '/api/push/key')).json()) as { key: string | null };
    if (!key) return 'unavailable';
    const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
    const { authHeaders } = await import('../net/progressApi');
    const res = await fetch(SERVER_HTTP + '/api/push/subscribe', {
      method: 'POST',
      headers: await authHeaders(false),
      body: JSON.stringify({ subscription: sub.toJSON(), language }),
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) throw new Error(`subscribe ${res.status}`);
    diag.mark('push: reminders on');
    return 'on';
  } catch (err) {
    diag.error(`push: ${String(err)}`);
    return 'failed';
  }
}

/** Turns reminders off in this browser. */
export async function disablePush(): Promise<void> {
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    const { authHeaders } = await import('../net/progressApi');
    await fetch(SERVER_HTTP + '/api/push/unsubscribe', {
      method: 'POST',
      headers: await authHeaders(false),
      body: JSON.stringify({ endpoint: sub.endpoint }),
      signal: AbortSignal.timeout(10_000),
    }).catch(() => undefined);
    await sub.unsubscribe();
  } catch (err) {
    diag.error(`push: ${String(err)}`);
  }
}
